import React, { Fragment, useEffect, useReducer, useState } from 'react';
import { isAfter, isBefore } from 'date-fns';
import { styled } from 'styled-components';
import { areRangesEqual, checkRangeValidity } from './date-picker-helper';
import { DatePicker } from './DatePicker';
import { initialState, reducer } from './dateRangeReducer';
import SvgCalendar from '../../../../play-core/icons/Calendar';
import { SecondaryButton } from '../ClickableComponents/Button';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DateRangePicker = ({
  range,
  restrictedRange,
  keepOpen = false,
  fullWidth = false,
  revealButtonText,
  revealButtonSize,
  markedDates = {},
  quickSelectDate,
  quickSelectText,
  showCalendarIcon = true,
  expand = false,
  flyoutStyle,
  allowOpenRange = false,
  isDisabled = false,
  onChange,
  onReveal
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const hasRevealButton = !!revealButtonText;
  const [isRevealed, setRevealed] = useState(!hasRevealButton);
  useEffect(() => dispatch({
    type: 'set-range',
    range
  }), [range]);
  useEffect(() => dispatch({
    type: 'set-keep-open',
    keepOpen
  }), [keepOpen]);
  useEffect(() => dispatch({
    type: 'set-allow-open-range',
    allowOpenRange
  }), [allowOpenRange]);
  useEffect(() => dispatch({
    type: 'set-restricted-range',
    range: restrictedRange
  }), [restrictedRange]);
  const emitChange = newRange => {
    const validity = checkRangeValidity(newRange, allowOpenRange);
    if (validity === 'Valid' && !areRangesEqual(newRange, range)) {
      onChange(newRange);
    }
  };
  return /*#__PURE__*/_jsx(Fragment, {
    children: isRevealed ? /*#__PURE__*/_jsxs(Layout, {
      fullWidth: fullWidth,
      children: [/*#__PURE__*/_jsx(DatePicker, {
        date: state.range.from,
        isOpen: state.isFromOpen,
        restrictedRange: state.restrictedRange,
        selectedRange: state.range,
        stateInformation: state.errorMessage ? {
          text: state.errorMessage,
          markFieldAsError: true
        } : undefined,
        keepOpen: keepOpen,
        fullWidth: fullWidth,
        showCalendarIcon: showCalendarIcon,
        markedDates: markedDates,
        inputStyle: 'RangeLeft',
        onChange: (date, source) => {
          const shouldSetToDate = !allowOpenRange && source === 'DatePicker' && date && state.range.to && isAfter(new Date(date), new Date(state.range.to));
          if (shouldSetToDate) {
            dispatch({
              type: 'set-to',
              date,
              source
            });
            emitChange({
              from: state.range.from,
              to: date
            });
          } else {
            dispatch({
              type: 'set-from',
              date,
              source
            });
            emitChange({
              from: date,
              to: state.range.to
            });
          }
        },
        onOpenChanged: isOpen => {
          dispatch({
            type: 'set-from-open',
            isOpen
          });
        },
        onValidityChanged: validity => dispatch({
          type: 'set-from-validity',
          validity
        }),
        quickSelectDate: quickSelectDate,
        quickSelectText: quickSelectText,
        expandInput: expand,
        flyoutStyle: flyoutStyle,
        isDisabled: isDisabled
      }), /*#__PURE__*/_jsx(DatePicker, {
        date: state.range.to,
        defaultDate: state.range.from,
        restrictedRange: state.restrictedRange,
        selectedRange: state.range,
        isOpen: state.isToOpen,
        keepOpen: keepOpen,
        fullWidth: fullWidth,
        markedDates: markedDates,
        inputStyle: 'RangeRight',
        onChange: (date, source) => {
          const shouldSetFromDate = !allowOpenRange && source === 'DatePicker' && date && state.range.from && isBefore(new Date(date), new Date(state.range.from));
          if (shouldSetFromDate) {
            dispatch({
              type: 'set-from',
              date,
              source
            });
            emitChange({
              from: date,
              to: state.range.to
            });
          } else {
            dispatch({
              type: 'set-to',
              date,
              source
            });
            emitChange({
              from: state.range.from,
              to: date
            });
          }
        },
        onOpenChanged: isOpen => {
          dispatch({
            type: 'set-to-open',
            isOpen
          });
        },
        onValidityChanged: validity => dispatch({
          type: 'set-to-validity',
          validity
        }),
        expandInput: expand,
        flyoutStyle: flyoutStyle,
        flyoutPosition: flyoutStyle === 'dense' ? 'right' : 'left',
        isDisabled: isDisabled
      })]
    }) : /*#__PURE__*/_jsx(SecondaryButton, {
      type: "text",
      text: revealButtonText || '',
      onClick: () => {
        setRevealed(true);
        onReveal && onReveal(true);
        dispatch({
          type: 'set-from-open',
          isOpen: true
        });
      },
      icon: /*#__PURE__*/_jsx(SvgCalendar, {}),
      isDisabled: isDisabled,
      size: revealButtonSize
    })
  });
};
const Layout = styled.div.withConfig({
  displayName: "DateRangePicker__Layout",
  componentId: "sc-w7f7gg-0"
})(["position:", ";display:flex;"], props => props.fullWidth ? 'relative' : 'unset');